import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Tooltip, FormControl, InputLabel, Select, MenuItem, Chip, Stack } from '@mui/material'
import { Add, QrCode2, ViewColumn, ViewColumnOutlined, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, PermissionGate, usePermissions, ExportButtons } from '@components/common'
import { AssetForm } from '@components/assets'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate, formatDateTime } from '@utils/dateUtils'

// Essential columns shown by default
// Order: name, category, serial number, model, tag number, location, room, status
const essentialColumns = [
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'category_name', headerName: 'Category', sortable: true, renderCell: (v) => v || '-' },
  { field: 'serial_number', headerName: 'Serial No.', sortable: true, renderCell: (v) => v || '-' },
  { field: 'model_number', headerName: 'Model', sortable: true, renderCell: (v) => v || '-' },
  { field: 'asset_tag', headerName: 'Tag Number', sortable: true, width: 140 },
  { field: 'location_name', headerName: 'Location', sortable: true, renderCell: (v) => v || '-' },
  { field: 'room_number', headerName: 'Room', sortable: true, renderCell: (v) => v || '-', width: 80 },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => <StatusChip status={value} />,
  },
]

// Additional columns shown when expanded
// Order: facility, unit_count, cost, purchase date, manufacturer, remarks
const additionalColumns = [
  { field: 'facility_name', headerName: 'Facility', sortable: true, renderCell: (v) => v || '-' },
  { field: 'unit_count', headerName: '# Units', sortable: true, width: 80, renderCell: (v) => v || 1 },
  {
    field: 'purchase_cost',
    headerName: 'Cost',
    sortable: true,
    renderCell: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '-'
  },
  {
    field: 'purchase_date',
    headerName: 'Purchase Date',
    sortable: true,
    renderCell: (v) => v ? formatDate(v) : '-'
  },
  { field: 'manufacturer_name', headerName: 'Manufacturer', sortable: true, renderCell: (v) => v || '-' },
  { field: 'notes', headerName: 'Remarks', sortable: true, renderCell: (v) => v ? (v.length > 30 ? v.substring(0, 30) + '...' : v) : '-' },
]

const AssetsList = () => {
  const navigate = useNavigate()
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [showAllColumns, setShowAllColumns] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const { canCreate } = usePermissions()

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    department: '',
  })

  // Filter options
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])

  // Memoize columns based on toggle state
  const columns = useMemo(() => {
    if (showAllColumns) {
      // All columns: essential + additional
      return [...essentialColumns, ...additionalColumns]
    }
    return essentialColumns
  }, [showAllColumns])

  // Export columns with export values
  const exportColumns = useMemo(() => {
    return [
      { field: 'name', headerName: 'Name' },
      { field: 'category_name', headerName: 'Category' },
      { field: 'serial_number', headerName: 'Serial No.' },
      { field: 'model_number', headerName: 'Model' },
      { field: 'asset_tag', headerName: 'Tag Number' },
      { field: 'location_name', headerName: 'Location' },
      { field: 'room_number', headerName: 'Room' },
      { field: 'facility_name', headerName: 'Facility' },
      { field: 'unit_count', headerName: '# Units', renderValue: (v) => v || 1 },
      { field: 'status', headerName: 'Status', renderValue: (v) => {
        const statusLabels = {
          'A': 'A − Good and in use',
          'B': 'B − Good but not in use',
          'C': 'C − In use but needs repair',
          'D': 'D − In use but needs maintenance',
          'E': 'E − Not in use but needs repair and maintenance',
          'F': 'F − Not functional, needs to be discarded',
        }
        return statusLabels[v] || v
      }},
      { field: 'purchase_cost', headerName: 'Cost', renderValue: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '' },
      { field: 'purchase_date', headerName: 'Purchase Date', renderValue: (v) => v ? formatDate(v) : '' },
      { field: 'manufacturer_name', headerName: 'Manufacturer' },
      { field: 'notes', headerName: 'Remarks' },
    ]
  }, [])

  const fetchAssets = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.status) params.status = filters.status
      if (filters.category) params.category = filters.category
      if (filters.department) params.department = filters.department

      const response = await api.get('/assets/assets/', { params })
      setAssets(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching assets:', error)
      toast.error('Failed to load assets')
    } finally {
      setLoading(false)
    }
  }

  const fetchFilterOptions = async () => {
    try {
      const [catRes, deptRes] = await Promise.all([
        api.get('/assets/categories/'),
        api.get('/common/departments/'),
      ])
      setCategories(catRes.data.results || catRes.data || [])
      setDepartments(deptRes.data.results || deptRes.data || [])
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  useEffect(() => {
    fetchAssets()
  }, [filters])

  useEffect(() => {
    fetchFilterOptions()
  }, [])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ status: '', category: '', department: '' })
  }

  const hasActiveFilters = filters.status || filters.category || filters.department

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.status) labels.Status = filters.status.replace(/_/g, ' ')
    if (filters.category) {
      const cat = categories.find(c => c.id === parseInt(filters.category))
      labels.Category = cat?.name || filters.category
    }
    if (filters.department) {
      const dept = departments.find(d => d.id === parseInt(filters.department))
      labels['Section/Unit'] = dept?.name || filters.department
    }
    return labels
  }

  const handleFormSuccess = () => {
    fetchAssets()
  }

  return (
    <Box>
      <PageHeader
        title="Asset Register"
        subtitle="Manage all equipment and assets"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Asset Register' },
        ]}
        actions={
          <>
            <ExportButtons
              data={assets}
              columns={exportColumns}
              title="Asset Register"
              filename="asset-register"
              filters={getActiveFilterLabels()}
            />
            <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                color={hasActiveFilters ? 'primary' : 'inherit'}
              >
                Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length})`}
              </Button>
            </Tooltip>
            <Tooltip title={showAllColumns ? 'Show fewer columns' : 'Show all columns'}>
              <Button
                variant="outlined"
                startIcon={showAllColumns ? <ViewColumn /> : <ViewColumnOutlined />}
                onClick={() => setShowAllColumns(!showAllColumns)}
                color={showAllColumns ? 'primary' : 'inherit'}
              >
                {showAllColumns ? 'Fewer Columns' : 'All Columns'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<QrCode2 />}
              onClick={() => navigate('/assets/scan')}
            >
              Scan QR
            </Button>
            <PermissionGate permission="assets.create">
              <Button
                startIcon={<Add />}
                onClick={() => setFormOpen(true)}
              >
                Add Asset
              </Button>
            </PermissionGate>
          </>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="A">A − Good and in use</MenuItem>
                <MenuItem value="B">B − Good but not in use</MenuItem>
                <MenuItem value="C">C − In use but needs repair</MenuItem>
                <MenuItem value="D">D − In use but needs maintenance</MenuItem>
                <MenuItem value="E">E − Not in use but needs repair and maintenance</MenuItem>
                <MenuItem value="F">F − Not functional, needs to be discarded</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={handleFilterChange('category')}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Section/Unit</InputLabel>
              <Select
                value={filters.department}
                onChange={handleFilterChange('department')}
                label="Section/Unit"
              >
                <MenuItem value="">All Sections/Units</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {Object.entries(getActiveFilterLabels()).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  onDelete={() => {
                    const fieldMap = { 'Status': 'status', 'Category': 'category', 'Section/Unit': 'department' }
                    setFilters(prev => ({ ...prev, [fieldMap[key]]: '' }))
                  }}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={assets}
          loading={loading}
          onRowClick={(row) => navigate(`/assets/${row.id}`)}
          searchPlaceholder="Search assets..."
          emptyMessage="No assets found"
        />
      </Card>

      <AssetForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </Box>
  )
}

export default AssetsList
